/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import cx from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';

/**
 * InputField component renders an input element with validation and error styling.
 *
 * @param {object} props - Component props.
 * @param {string} props.name - Name of the input field.
 * @param {string} [props.dataTestId='textbox'] - Test ID for the input.
 * @param {string} [props.type='text'] - Type of the input.
 * @param {string} [props.placeholder=null] - Placeholder text for the input.
 * @param {boolean} [props.isRequired=false] - Whether the field is required.
 * @param {Function} [props.onBlur=null] - Blur event handler for the input.
 * @param {string} [props.className=''] - Additional class name(s) for the input container.
 * @returns {JSX.Element} The rendered input element.
 */
const InputField = ({
  name,
  dataTestId = 'textbox',
  type = 'text',
  placeholder = null,
  isRequired = false,
  onBlur = null,
  className = '',
  ...props
}) => {
  const {
    watch,
    control,
  } = useFormContext();

  const controllerSettings = {
    name,
    control,
    rules: { required: isRequired },
  };

  if (type === 'checkbox') {
    // set default value to correctly handle validation for checkboxes
    controllerSettings.defaultValue = false;
  }

  const {
    field,
    fieldState: { error },
  } = useController(controllerSettings);

  return (
    <div className={cx({
      [styles.inputContainer]: type !== 'checkbox',
      [className]: className,
    })}
    >
      {placeholder ? (
        <span className={cx(styles.placeholder, {
          [styles.visible]: watch(name),
        })}
        >
          {placeholder}
        </span>
      ) : null}
      <input
        className={cx(styles.input, {
          [styles.errorInput]: error,
          [styles.hasSuperTxt]: placeholder && watch(name),
        })}
        data-testid={dataTestId}
        name={name}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        {...props}
        {...field}
        onBlur={(e) => {
          if (onBlur) onBlur(e);
          // Call the field's onBlur method to trigger validation
          field.onBlur(e);
        }}
      />
    </div>
  );
};

export { InputField };
